import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlusCircle, Edit2, Trash2, Save, X, Loader2,
  ChevronUp, ChevronDown, Building2, Calendar, DollarSign,
  CheckCircle, Zap, Users, FileText, Shield, ArrowRight, Star, Globe,
  List, ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

const ICON_OPTIONS = [
  'Building2', 'Calendar', 'DollarSign', 'CheckCircle', 'Zap',
  'Users', 'FileText', 'Shield', 'ArrowRight', 'Star', 'Globe',
];

const ICON_MAP = {
  Building2, Calendar, DollarSign, CheckCircle, Zap,
  Users, FileText, Shield, ArrowRight, Star, Globe,
};

function StepIcon({ name, size = 18, className = '' }) {
  const IconComp = ICON_MAP[name] || Zap;
  return <IconComp size={size} className={className} />;
}

function StepModal({ step, onClose, onSaved }) {
  const isEdit = Boolean(step?.id);
  const token = localStorage.getItem('token');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    step_number: step?.step_number ?? 1,
    icon_name: step?.icon_name || 'Zap',
    title: step?.title || '',
    description: step?.description || '',
    tags: Array.isArray(step?.tags) ? step.tags.join(', ') : (step?.tags || ''),
    gradient_from: step?.gradient_from || '#6366f1',
    gradient_to: step?.gradient_to || '#8b5cf6',
    sort_order: step?.sort_order ?? 0,
    is_active: step?.is_active !== false,
    scheduled_at: step?.scheduled_at || null,
  });

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  const parsedTags = form.tags
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        step_number: parseInt(form.step_number) || 1,
        sort_order: parseInt(form.sort_order) || 0,
        tags: parsedTags,
        scheduled_at: form.scheduled_at || null,
      };
      const url = isEdit
        ? `${API}/api/marketing/how-it-works/${step.id}`
        : `${API}/api/marketing/how-it-works`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Save failed');
      toast.success(isEdit ? 'Step updated' : 'Step created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-100 shrink-0">
          <h2 className="text-lg font-bold text-slate-900">{isEdit ? 'Edit Step' : 'Add Step'}</h2>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Step Number</label>
              <input type="number" min={1} max={10} value={form.step_number} onChange={set('step_number')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Icon</label>
              <select value={form.icon_name} onChange={set('icon_name')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30">
                {ICON_OPTIONS.map(ic => (
                  <option key={ic} value={ic}>{ic}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Title *</label>
              <input type="text" value={form.title} onChange={set('title')} required
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                placeholder="e.g. Schedule in seconds" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
              <textarea value={form.description} onChange={set('description')} rows={3}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 resize-none"
                placeholder="Brief description of this step" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Tags (comma-separated)</label>
              <input type="text" value={form.tags} onChange={set('tags')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                placeholder="e.g. Fast, Easy, Automated" />
              {parsedTags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {parsedTags.map(tag => (
                    <span key={tag} className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">{tag}</span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Gradient From</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.gradient_from} onChange={set('gradient_from')}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                <input type="text" value={form.gradient_from} onChange={set('gradient_from')}
                  className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 font-mono" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Gradient To</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.gradient_to} onChange={set('gradient_to')}
                  className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer p-0.5" />
                <input type="text" value={form.gradient_to} onChange={set('gradient_to')}
                  className="flex-1 px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30 font-mono" />
              </div>
            </div>
            {/* Gradient preview */}
            <div className="col-span-2">
              <div className="h-10 rounded-xl border border-slate-200"
                style={{ background: `linear-gradient(135deg, ${form.gradient_from}, ${form.gradient_to})` }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sort Order</label>
              <input type="number" min={0} value={form.sort_order} onChange={set('sort_order')}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/30" />
            </div>
            <div className="flex items-center gap-3 pt-5">
              <input type="checkbox" id="hiw_active" checked={form.is_active}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                className="w-4 h-4 accent-primary-600 rounded" />
              <label htmlFor="hiw_active" className="text-sm font-medium text-slate-700">Active</label>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Schedule publish date (optional)</label>
              <p className="text-xs text-slate-400 mb-2">Leave empty to publish immediately when active. Set a future date to auto-publish at that time.</p>
              <input
                type="datetime-local"
                value={form.scheduled_at ? form.scheduled_at.slice(0, 16) : ''}
                onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value || null }))}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400"
              />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Step'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function HowItWorksEditor() {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const token = localStorage.getItem('token');

  async function fetchSteps() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/marketing/how-it-works/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSteps(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load steps');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchSteps(); }, []);

  async function deleteStep(id, title) {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      const res = await fetch(`${API}/api/marketing/how-it-works/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Step deleted');
      fetchSteps();
    } catch {
      toast.error('Failed to delete');
    }
  }

  async function toggleActive(step) {
    try {
      await fetch(`${API}/api/marketing/how-it-works/${step.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...step, is_active: !step.is_active }),
      });
      fetchSteps();
    } catch {
      toast.error('Failed to update');
    }
  }

  async function reorder(index, direction) {
    const newSteps = [...steps];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newSteps.length) return;
    [newSteps[index], newSteps[swapIndex]] = [newSteps[swapIndex], newSteps[index]];
    setSteps(newSteps);
    try {
      await Promise.all(
        newSteps.map((s, i) =>
          fetch(`${API}/api/marketing/how-it-works/${s.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...s, sort_order: i }),
          })
        )
      );
      fetchSteps();
    } catch {
      toast.error('Failed to reorder');
    }
  }

  return (
    <div className="p-6 space-y-6">
      {modal && (
        <StepModal
          step={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={fetchSteps}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">How It Works</h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage the 3-step process shown on the homepage</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${MARKETING_URL}/#how-it-works`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-teal-600 border border-teal-200 rounded-xl hover:bg-teal-50 transition-colors">
            <ExternalLink size={14} />
            Preview on Site
          </a>
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm">
            <PlusCircle size={16} />
            Add Step
          </button>
        </div>
      </div>

      {/* Steps grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : steps.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400 bg-white rounded-2xl border border-slate-200">
          <List size={40} className="mb-3 opacity-30" />
          <p className="font-medium">No steps yet</p>
          <p className="text-sm mt-1">Click "Add Step" to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {steps.map((step, index) => {
            const gradFrom = step.gradient_from || '#6366f1';
            const gradTo = step.gradient_to || '#8b5cf6';
            const tags = Array.isArray(step.tags)
              ? step.tags
              : typeof step.tags === 'string' && step.tags
                ? step.tags.split(',').map(t => t.trim()).filter(Boolean)
                : [];
            const isScheduled = step.scheduled_at && new Date(step.scheduled_at) > new Date();

            return (
              <div key={step.id}
                className={clsx(
                  'bg-white rounded-2xl border shadow-sm flex flex-col overflow-hidden transition-all',
                  step.is_active ? 'border-slate-200' : 'border-slate-200 opacity-60'
                )}>
                {/* Gradient top bar */}
                <div className="h-1.5 w-full"
                  style={{ background: `linear-gradient(90deg, ${gradFrom}, ${gradTo})` }} />

                <div className="p-5 flex-1 space-y-3">
                  {/* Step badge + icon */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: `linear-gradient(135deg, ${gradFrom}, ${gradTo})` }}>
                      <StepIcon name={step.icon_name} size={18} className="text-white" />
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <div>
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white shrink-0"
                          style={{ background: `linear-gradient(135deg, ${gradFrom}, ${gradTo})` }}>
                          {step.step_number}
                        </span>
                        <span className="ml-1.5 text-xs text-slate-400 font-medium uppercase tracking-wider">Step {step.step_number}</span>
                      </div>
                      {isScheduled && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                          Scheduled
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-base font-bold text-slate-900 leading-snug">{step.title}</h3>
                    {step.description && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{step.description}</p>
                    )}
                  </div>

                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                          style={{ background: `${gradFrom}18`, color: gradFrom }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Card footer actions */}
                <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
                  <div className="flex items-center gap-1">
                    <button onClick={() => reorder(index, 'up')} disabled={index === 0}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-30">
                      <ChevronUp size={14} />
                    </button>
                    <button onClick={() => reorder(index, 'down')} disabled={index === steps.length - 1}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-30">
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleActive(step)}
                      className={clsx(
                        'px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors',
                        step.is_active
                          ? 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      )}>
                      {step.is_active ? 'Active' : 'Hidden'}
                    </button>
                    <button onClick={() => setModal(step)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => deleteStep(step.id, step.title)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
